import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button, Card, Badge } from '../components/ui';
import { SANDBOX_SCENARIOS, SandboxScenario } from '../data/scenarios.config';
import { generatePersonaResponse, AIResponse } from '../services/gemini';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, setDoc } from 'firebase/firestore';

interface ChatMessage {
  id: string;
  role: 'system' | 'model' | 'user';
  content: string;
  composureDelta?: number;
  childCentricDelta?: number;
  jargonBuster?: string;
  nextStepHint?: string;
}

const LiPSandbox = () => {
    const { userProfile, currentUser } = useAuth();
    
    // State
    const [selectedScenario, setSelectedScenario] = useState<SandboxScenario | null>(null);
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    
    // Determine if we are in Stripe Sandbox/Test Mode
    const [isStripeSandbox, setIsStripeSandbox] = useState(() => {
        return window.location.hostname === 'localhost' ||
            window.location.hostname.includes('127.0.0.1') ||
            window.location.search.includes('sandbox=true') || 
            window.location.hash.includes('sandbox=true');
    });

    useEffect(() => {
        const fetchGlobalSettings = async () => {
            try {
                const docRef = doc(db, 'config', 'app_settings');
                const snap = await getDoc(docRef);
                if (snap.exists() && snap.data().stripeTestMode === true) {
                    setIsStripeSandbox(true);
                }
            } catch (e) {
                console.log("Could not fetch global settings.");
            }
        };
        fetchGlobalSettings();
    }, []);
    
    // Auth & Access
    // Removed admin override so that you can freely toggle sandboxAccess to test the paywall
    const isAuthorized = userProfile?.sandboxAccess === true;

    // Handle Callback from Stripe
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        // also check hash params if hash router is used
        const hashStr = window.location.hash.split('?')[1] || '';
        const hashParams = new URLSearchParams(hashStr);
        
        if ((searchParams.get('sandbox_success') === 'true' || hashParams.get('sandbox_success') === 'true') && userProfile?.uid) {
             const userRef = doc(db, 'users', userProfile.uid);
             const expiry = new Date();
             expiry.setFullYear(expiry.getFullYear() + 1);
             
             updateDoc(userRef, { 
                 sandboxAccess: true,
                 sandboxExpiry: expiry.toISOString()
             })
                .then(() => {
                    // Update URL silently
                    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
                })
                .catch(console.error);
        }
    }, [userProfile?.uid]);

    const handlePurchase = async () => {
        if (!userProfile) return;
        setLoadingPayment(true);
        const paymentLink = isStripeSandbox
            ? 'https://buy.stripe.com/test_3cIaEWaBEfa078S0dI2cg03'
            : 'https://buy.stripe.com/3cI8wOfVY7HygJs3pU2cg06';
        let emailParam = '';
        if (currentUser && currentUser.email) {
            emailParam = `?prefilled_email=${encodeURIComponent(currentUser.email)}`;
        }
        
        const finalUrl = `${paymentLink}${emailParam ? emailParam + '&' : '?'}client_reference_id=${userProfile.uid}`;
        window.location.href = finalUrl;
    };
    
    // Metrics
    const [composureScore, setComposureScore] = useState(100);
    const [childCentricScore, setChildCentricScore] = useState(100);
    const [turnCount, setTurnCount] = useState(0);

    // Coach's Corner State
    const [showCoach, setShowCoach] = useState(false);
    const [coachFeedback, setCoachFeedback] = useState('');
    const [isCritical, setIsCritical] = useState(false);
    const [suggestion, setSuggestion] = useState('');
    const [hasFinished, setHasFinished] = useState(false);
    const [showMobileMetrics, setShowMobileMetrics] = useState(false);

    const chatEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    useEffect(() => {
        if (inputText === '' && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [inputText]);

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputText(e.target.value);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (inputText.trim() && !isTyping && !showCoach && !hasFinished) {
                 handleSendMessage();
            }
        }
    };

    // Auto-scroll
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, isTyping]);

    const startScenario = (scenario: SandboxScenario) => {
        setSelectedScenario(scenario);
        setComposureScore(100);
        setChildCentricScore(100);
        setTurnCount(0);
        setShowCoach(false);
        setHasFinished(false);
        setChatHistory([
            { id: 'ctx', role: 'system', content: scenario.context },
            { 
                id: 'init', 
                role: 'model', 
                content: scenario.initialMessage,
                jargonBuster: scenario.initialJargon,
                nextStepHint: scenario.initialHint
            }
        ]);
    };

    const handleSendMessage = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!inputText.trim() || !selectedScenario || isTyping || showCoach) return;

        const userMsg = inputText.trim();
        setInputText('');
        setTurnCount(prev => prev + 1);
        
        const newHistory = [...chatHistory, { id: Date.now().toString(), role: 'user' as const, content: userMsg }];
        setChatHistory(newHistory);
        setIsTyping(true);

        try {
            // Filter out system messages for the API
            const apiHistory = newHistory.filter(m => m.role !== 'system').map(m => ({
                role: m.role,
                content: m.content
            }));

            const isEndOfScenario = (turnCount + 1) >= 5; // We use turnCount BEFORE adding the current message, so if it was 4, this is the 5th user message.

            const response: AIResponse = await generatePersonaResponse(
                selectedScenario.persona,
                selectedScenario.context,
                apiHistory,
                isEndOfScenario
            );

            // Update Metrics
            const compDelta = Number(response.composureDelta) || 0;
            const childDelta = Number(response.childCentricDelta) || 0;

            setComposureScore(prev => Math.max(0, Math.min(100, prev + compDelta)));
            setChildCentricScore(prev => Math.max(0, Math.min(100, prev + childDelta)));

            // Add Model Reply
            setChatHistory(prev => [...prev, { 
                id: Date.now().toString(), 
                role: 'model', 
                content: response.reply,
                composureDelta: compDelta,
                childCentricDelta: childDelta,
                jargonBuster: response.jargonBuster,
                nextStepHint: response.nextStepHint
            }]);

            if (response.isCriticalFailure || isEndOfScenario) {
                setTimeout(() => {
                    setCoachFeedback(response.feedback || (isEndOfScenario ? "Scenario completed. You survived the interaction. Review your metrics to see how well you maintained composure and child focus under pressure." : "Critical legal or BIFF framework error detected."));
                    setIsCritical(response.isCriticalFailure);
                    setSuggestion(response.alternativeSuggestion || '');
                    setHasFinished(true);
                    setShowCoach(true);
                }, 1000);
            }

        } catch (error) {
            console.error(error);
        } finally {
            setIsTyping(false);
        }
    };

    const resetSelection = () => {
        setSelectedScenario(null);
        setChatHistory([]);
        setShowCoach(false);
        setHasFinished(false);
    };

    const handleTryAgainFromHere = () => {
        if (!isCritical) return;
        const lastMsg = chatHistory[chatHistory.length - 1];
        if (lastMsg && lastMsg.role === 'model') {
            const compDelta = Number(lastMsg.composureDelta) || 0;
            const childDelta = Number(lastMsg.childCentricDelta) || 0;
            setComposureScore(prev => Math.max(0, Math.min(100, prev - compDelta)));
            setChildCentricScore(prev => Math.max(0, Math.min(100, prev - childDelta)));
        }
        setChatHistory(prev => prev.slice(0, -2));
        setTurnCount(prev => Math.max(0, prev - 1));
        setShowCoach(false);
        if (suggestion) {
            setInputText(suggestion);
        }
    };

    // --- Sub-components ---

    const BiometricsPanelContent = () => {
        const lastModelMsg = [...chatHistory].reverse().find(m => m.role === 'model');
        const activeJargon = lastModelMsg?.jargonBuster;
        const activeHint = lastModelMsg?.nextStepHint;

        return (
            <>
                <div>
                    <h3 className="text-md-on-surface font-bold text-lg mb-1">Metrics</h3>
                    <p className="text-[10px] text-md-on-surface-variant font-medium tracking-widest uppercase mb-3">Live Assessment</p>
                    
                    <div className="space-y-4">
                        {/* Composure Meter */}
                        <div>
                            <div className="flex justify-between text-xs font-bold mb-2">
                                <span className="text-md-on-surface">Composure</span>
                                <span className={composureScore > 50 ? 'text-md-success' : 'text-md-error'}>{composureScore}%</span>
                            </div>
                            <div className="h-3 w-full bg-md-surface-variant rounded-full overflow-hidden">
                                <motion.div 
                                    className={`h-full ${composureScore > 70 ? 'bg-md-success' : composureScore > 30 ? 'bg-amber-400' : 'bg-md-error'}`}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${composureScore}%` }}
                                    transition={{ type: "spring", bounce: 0.2 }}
                                />
                            </div>
                        </div>

                        {/* Child-Centric Focus */}
                        <div>
                            <div className="flex justify-between text-xs font-bold mb-2">
                                <span className="text-md-on-surface">Child Focus</span>
                                <span className={childCentricScore > 50 ? 'text-md-primary' : 'text-md-error'}>{childCentricScore}%</span>
                            </div>
                            <div className="h-3 w-full bg-md-surface-variant rounded-full overflow-hidden">
                                <motion.div 
                                    className={`h-full ${childCentricScore > 70 ? 'bg-md-primary' : childCentricScore > 30 ? 'bg-amber-400' : 'bg-md-error'}`}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${childCentricScore}%` }}
                                    transition={{ type: "spring", bounce: 0.2 }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-md-outline-variant/30">
                    <div className="text-[10px] font-bold tracking-widest uppercase text-md-on-surface-variant mb-2">Opponent Info</div>
                    <div className="flex items-center gap-3 bg-md-surface rounded-2xl p-2.5 shadow-sm border border-md-outline/5">
                        <div className="w-8 h-8 rounded-full bg-md-secondary-container flex items-center justify-center text-lg shrink-0">
                            {selectedScenario?.persona === 'judge' ? '⚖️' : selectedScenario?.persona === 'social_worker' ? '📋' : selectedScenario?.persona === 'barrister' ? '💼' : selectedScenario?.persona === 'cafcass' ? '📝' : '👤'}
                        </div>
                        <div>
                            <p className="text-xs font-bold text-md-on-surface capitalize">{selectedScenario?.persona.replace('_', ' ')}</p>
                            <p className="text-[9px] text-md-on-surface-variant font-medium uppercase tracking-wider">{selectedScenario?.difficulty} Level</p>
                        </div>
                    </div>
                </div>
                
                {activeJargon && (
                    <div className="pt-4 border-t border-md-outline-variant/30 flex-shrink-0">
                        <p className="text-[10px] font-black tracking-widest uppercase text-md-tertiary mb-1.5">Jargon Buster</p>
                        <div className="bg-md-tertiary-container/40 border border-md-tertiary/20 rounded-2xl p-3 shadow-sm">
                            <p className="text-xs text-md-on-surface font-medium leading-relaxed">{activeJargon}</p>
                        </div>
                    </div>
                )}
                
                {activeHint && !hasFinished && (
                    <div className="pt-4 border-t border-md-outline-variant/30 flex-shrink-0">
                        <p className="text-[10px] font-black tracking-widest uppercase text-emerald-600 mb-1.5">Coach's Hint (Next Steps)</p>
                        <div className="bg-emerald-50 border border-emerald-500/20 rounded-2xl p-3 shadow-sm">
                            <p className="text-xs text-emerald-950 font-medium leading-relaxed">{activeHint}</p>
                        </div>
                    </div>
                )}

                <div className="mt-auto shrink-0 pt-3">
                    <Button variant="outline" onClick={resetSelection} className="w-full !rounded-full !py-2.5 text-xs">End Simulation</Button>
                </div>
            </>
        );
    };

    const CoachsCorner = () => (
        <AnimatePresence>
            {showCoach && (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-md-on-surface/40 backdrop-blur-sm"
                    />
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className={`bg-md-surface-container overflow-hidden rounded-[32px] p-8 max-w-lg w-full shadow-2xl relative z-10 border ${isCritical ? 'border-md-outline-variant/50' : 'border-amber-500/50 bg-gradient-to-b from-amber-500/5 to-transparent'}`}
                    >
                        {!isCritical && (
                            <>
                                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400"></div>
                                <div className="absolute -top-8 -right-8 text-8xl opacity-20 transform rotate-12">🏆</div>
                            </>
                        )}
                        <div className="flex items-center gap-4 mb-6 relative z-10">
                            <div className={`w-16 h-16 shrink-0 rounded-[20px] flex items-center justify-center text-3xl shadow-inner ${isCritical ? 'bg-md-success/20 text-md-success' : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg'}`}>
                                {isCritical ? '💡' : '🎉'}
                            </div>
                            <div>
                                {isCritical ? (
                                    <>
                                        <h2 className="text-xl font-black text-md-on-surface">Coach's Corner</h2>
                                        <p className="text-xs font-bold tracking-widest uppercase text-md-success">Let's try that again</p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-orange-600 tracking-tight leading-none mb-1">CONGRATULATIONS!</h2>
                                        <p className="text-[10px] font-black tracking-widest uppercase text-amber-600/80">Scenario Mastered</p>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div className={`rounded-2xl p-5 mb-6 text-sm font-medium leading-relaxed relative z-10 shadow-sm border ${isCritical ? 'bg-md-surface border-md-outline/10 text-md-on-surface' : 'bg-white border-amber-500/20 text-md-on-surface border-l-4 border-l-amber-500'}`}>
                            {coachFeedback || "You let your emotions override your child-centric focus. Remember the BIFF framework."}
                        </div>

                        {suggestion && (
                            <div className="bg-md-primary/10 rounded-2xl p-5 mb-6 border border-md-primary/20 text-sm font-medium text-md-on-surface leading-relaxed">
                                <p className="text-[10px] font-black tracking-widest uppercase text-md-primary mb-2">Try Something Like This Instead</p>
                                "{suggestion}"
                                <p className="text-xs text-md-on-surface-variant mt-2 italic">We can fill this in for you so you can try again.</p>
                            </div>
                        )}

                        <div className="flex gap-3 relative z-10">
                            {isCritical ? (
                                <>
                                    <Button className="flex-1 !rounded-full !py-3" onClick={handleTryAgainFromHere}>Try Again From Here</Button>
                                    <Button variant="outline" className="flex-1 !rounded-full !py-3" onClick={() => { setShowCoach(false); startScenario(selectedScenario!); }}>Restart Scenario</Button>
                                </>
                            ) : (
                                <>
                                    <Button className="flex-1 !rounded-full !py-3 bg-amber-500 hover:bg-amber-600 text-white shadow-md border-0" onClick={() => { setShowCoach(false); startScenario(selectedScenario!); }}>Play Again</Button>
                                    <Button variant="outline" className="flex-1 !rounded-full !py-3" onClick={resetSelection}>Choose Another</Button>
                                </>
                            )}
                        </div>
                        <div className="mt-4 flex justify-center relative z-10">
                            <Button variant="ghost" className="!rounded-full !py-2 text-xs font-bold w-full border border-md-on-surface/10" onClick={() => setShowCoach(false)}>Hide & Review Chat History</Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    if (!isAuthorized) {
        return (
            <Layout>
                <div className="w-full max-w-3xl mx-auto pt-16 px-4">
                    <Card className="p-10 text-center animate-slide-up border-0 shadow-2xl ring-1 ring-black/5 overflow-hidden relative group">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 to-orange-500"></div>
                        <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 text-orange-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-8 shadow-inner border border-white">
                            🔒
                        </div>
                        <h1 className="text-3xl font-heading font-black text-slate-900 mb-4">LiP Sandbox Court Simulator</h1>
                        <p className="text-slate-600 font-medium mb-8 leading-relaxed max-w-xl mx-auto">
                            The LiP Sandbox is our premium desensitization AI trainer. Practice brutal cross-examinations, hostile handovers, and Cafcass interviews against 4 distinct AI personas. Receive real-time scoring on composure and BIFF framework feedback.
                        </p>
                        
                        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 mb-8 max-w-sm mx-auto">
                            <h3 className="text-[10px] font-black tracking-[0.2em] text-slate-400 uppercase mb-2">One-Time Bolt-On</h3>
                            <div className="flex items-end justify-center gap-1">
                                <span className="text-5xl font-black text-slate-900">£45</span>
                                <span className="text-slate-500 font-bold mb-1">1-year access</span>
                            </div>
                        </div>

                        <div className="flex justify-center flex-col items-center">
                            <Button 
                                className="w-full max-w-sm !py-4 shadow-xl shadow-orange-500/20 bg-amber-500 hover:bg-amber-600 border-0" 
                                onClick={handlePurchase}
                                disabled={loadingPayment}
                            >
                                {loadingPayment ? 'Connecting to Stripe...' : 'Unlock Sandbox Now'}
                            </Button>
                            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-4">Secure checkout powered by Stripe</p>
                        </div>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="w-full max-w-6xl mx-auto h-[calc(100vh-100px)] flex flex-col pt-4 pb-8">
                
                {/* Header */}
                <div className="mb-6 px-2 flex justify-between items-end">
                    <div>
                        <Badge color="blue">Premium Feature</Badge>
                        <h1 className="text-3xl font-heading font-black text-md-on-surface mt-3">LiP Sandbox</h1>
                        <p className="text-sm text-md-on-surface-variant font-medium mt-1">Simulated training environment for litigants in person.</p>
                    </div>
                </div>

                {!selectedScenario ? (
                    /* Scenario Selection Grid */
                    <div className="flex-1 overflow-y-auto custom-scrollbar px-2 pb-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {SANDBOX_SCENARIOS.map(scenario => (
                                <Card key={scenario.id} className="p-6 flex flex-col cursor-pointer hover:bg-md-surface-variant transition-colors group" onClick={() => startScenario(scenario)}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-md-secondary-container text-md-on-secondary-container">
                                            {scenario.persona === 'judge' ? '⚖️' : scenario.persona === 'social_worker' ? '📋' : scenario.persona === 'barrister' ? '💼' : scenario.persona === 'cafcass' ? '📝' : '👤'}
                                        </div>
                                        <Badge color={scenario.difficulty === 'Beginner' ? 'green' : scenario.difficulty === 'Intermediate' ? 'blue' : scenario.difficulty === 'Expert' ? 'yellow' : 'red'}>
                                            {scenario.difficulty}
                                        </Badge>
                                    </div>
                                    <h3 className="text-lg font-bold text-md-on-surface tracking-tight leading-tight mb-2 group-hover:text-md-primary transition-colors">{scenario.title}</h3>
                                    <p className="text-xs text-md-on-surface-variant font-medium leading-relaxed mb-6 flex-1">{scenario.description}</p>
                                    <div className="text-[10px] font-black uppercase tracking-widest text-md-primary flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                                        Start Scenario <span className="text-sm">→</span>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                ) : (
                    /* Active Simulation Arena */
                    <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden relative">
                        {/* Simulation Chat Arena */}
                        <div className="flex-1 flex flex-col bg-md-surface-container rounded-[32px] overflow-hidden shadow-sm border border-md-outline/10 h-full relative">
                            
                            {/* Arena Header */}
                            <div className="bg-md-primary text-md-on-primary px-6 py-4 flex items-center justify-between shadow-md z-10 shrink-0">
                                <div>
                                    <h2 className="font-bold text-sm tracking-wide">{selectedScenario.title}</h2>
                                    <p className="text-[10px] font-medium opacity-80 uppercase tracking-widest">Live Integration Active</p>
                                </div>
                                <div className="flex gap-3 items-center">
                                    <Button 
                                        onClick={() => setShowMobileMetrics(true)}
                                        className="md:hidden bg-white/20 hover:bg-white/30 text-white !rounded-full !px-4 !py-1 text-[10px] font-black tracking-widest uppercase border-0 shadow-none h-auto"
                                    >
                                        Live Metrics
                                    </Button>
                                    <div className="text-2xl animate-pulse hidden md:block">🔴</div>
                                </div>
                            </div>

                            {/* Chat Messages */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-md-surface-container-lowest">
                                {chatHistory.map((msg, i) => {
                                    if (msg.role === 'system') {
                                        return (
                                            <div key={msg.id} className="bg-gradient-to-r from-md-tertiary-container/80 to-md-secondary-container/80 border-2 border-md-tertiary/30 rounded-3xl p-6 text-center mx-auto w-full shadow-md my-4 relative overflow-hidden">
                                                <div className="absolute -top-4 -right-4 p-4 opacity-[0.03] text-8xl transform -rotate-12">📍</div>
                                                <div className="flex items-center justify-center gap-2 mb-3 relative z-10">
                                                    <span className="text-xl">📍</span>
                                                    <h3 className="text-sm font-black text-md-on-surface uppercase tracking-widest">Scenario Context</h3>
                                                </div>
                                                <p className="text-sm md:text-base text-md-on-surface-variant font-medium leading-relaxed relative z-10">{msg.content}</p>
                                            </div>
                                        );
                                    }

                                    const isUser = msg.role === 'user';
                                    return (
                                        <motion.div 
                                            key={msg.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full`}
                                        >
                                            <div className={`flex gap-3 max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shadow-sm ${isUser ? 'bg-md-primary text-md-on-primary' : 'bg-md-secondary-container text-md-on-secondary-container'}`}>
                                                        {isUser ? (userProfile?.displayName?.charAt(0) || 'U') : (selectedScenario.persona === 'judge' ? '⚖️' : '👤')}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className={`text-[10px] font-bold tracking-widest uppercase ${isUser ? 'text-right text-md-primary' : 'text-left text-md-on-surface-variant'}`}>
                                                        {isUser ? 'You' : selectedScenario.persona.replace('_', ' ')}
                                                    </span>
                                                    <div className={`p-4 rounded-[24px] text-sm leading-relaxed shadow-sm whitespace-pre-wrap ${
                                                        isUser 
                                                            ? 'bg-md-primary text-md-on-primary rounded-tr-sm' 
                                                            : 'bg-md-surface text-md-on-surface border border-md-outline/10 rounded-tl-sm'
                                                    }`}>
                                                        {msg.content}
                                                    </div>
                                                    
                                                    {/* Display Metric Impacts explicitly below the AI response to show feedback loop */}
                                                    {!isUser && msg.composureDelta !== undefined && msg.composureDelta !== 0 && (
                                                        <div className="flex gap-2 mt-1 px-2">
                                                            <span className={`text-[9px] font-bold uppercase tracking-widest ${msg.composureDelta > 0 ? 'text-md-success' : 'text-md-error'}`}>
                                                                {msg.composureDelta > 0 ? '+' : ''}{msg.composureDelta} Composure
                                                            </span>
                                                        </div>
                                                    )}
                                                    
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                                {isTyping && (
                                    <div className="flex justify-start w-full">
                                        <div className="flex gap-3 max-w-[85%] flex-row">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm shadow-sm bg-md-secondary-container">⏳</div>
                                            </div>
                                            <div className="bg-md-surface rounded-[24px] rounded-tl-sm p-4 border border-md-outline/10 flex items-center gap-1">
                                                <div className="w-2 h-2 rounded-full bg-md-primary/60 animate-bounce"></div>
                                                <div className="w-2 h-2 rounded-full bg-md-primary/60 animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                                                <div className="w-2 h-2 rounded-full bg-md-primary/60 animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Input Area */}
                            <div className="p-4 bg-md-surface-container border-t border-md-outline/10 shrink-0">
                                {hasFinished && !showCoach ? (
                                    <div className="flex justify-center">
                                        <Button 
                                            onClick={() => setShowCoach(true)} 
                                            className="w-full max-w-sm !rounded-full !py-4 shadow-md font-bold text-sm tracking-wide"
                                        >
                                            Review Assessment
                                        </Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
                                        <textarea
                                            ref={textareaRef}
                                            value={inputText}
                                            onChange={handleInput}
                                            onKeyDown={handleKeyDown}
                                            placeholder={isTyping ? "Awaiting response..." : "Type your response here... (Shift+Enter for newline)"}
                                            disabled={isTyping || showCoach || hasFinished}
                                            rows={1}
                                            className="flex-1 bg-md-surface-container-highest text-md-on-surface border-none rounded-3xl px-6 py-4 focus:ring-2 focus:ring-md-primary outline-none text-sm placeholder:text-md-on-surface-variant/50 transition-all font-medium disabled:opacity-50 shadow-inner resize-none custom-scrollbar"
                                            style={{ minHeight: '52px', maxHeight: '160px' }}
                                        />
                                        <Button 
                                            type="submit" 
                                            disabled={!inputText.trim() || isTyping || showCoach || hasFinished}
                                            className="!rounded-full px-6 py-4 shadow-md hover:shadow-lg disabled:shadow-none transition-all h-[52px]"
                                        >
                                            <span className="text-xl leading-none">↗</span>
                                        </Button>
                                    </form>
                                )}
                                {!hasFinished && (
                                    <div className="text-center mt-3">
                                        <span className="text-[10px] font-bold text-md-on-surface-variant uppercase tracking-widest">Remember BIFF: Brief, Informative, Friendly, Firm</span>
                                    </div>
                                )}
                            </div>

                            <CoachsCorner />
                        </div>

                        {/* Desktop Side Panel */}
                        <div className="hidden md:flex md:w-80 bg-md-surface-container-low rounded-[24px] p-5 flex-col gap-4 shadow-sm border border-md-outline/10 h-fit max-h-full overflow-y-auto custom-scrollbar shrink-0">
                            <BiometricsPanelContent />
                        </div>
                        
                        {/* Mobile Side Panel Modal */}
                        <AnimatePresence>
                            {showMobileMetrics && (
                                <div className="fixed inset-0 z-50 flex flex-col justify-end p-2 md:hidden">
                                    <motion.div 
                                        initial={{ opacity: 0 }} 
                                        animate={{ opacity: 1 }} 
                                        exit={{ opacity: 0 }}
                                        className="absolute inset-0 bg-md-on-surface/40 backdrop-blur-sm"
                                        onClick={() => setShowMobileMetrics(false)}
                                    />
                                    <motion.div 
                                        initial={{ y: "100%" }} 
                                        animate={{ y: 0 }} 
                                        exit={{ y: "100%" }}
                                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                        className="relative z-10 bg-md-surface-container-low rounded-t-[32px] p-5 max-h-[85vh] overflow-y-auto custom-scrollbar shadow-2xl flex flex-col gap-4"
                                    >
                                        <div className="flex justify-between items-center shrink-0">
                                            <h2 className="font-black text-xl text-md-on-surface">Live Assessment</h2>
                                            <button 
                                                onClick={() => setShowMobileMetrics(false)} 
                                                className="w-10 h-10 rounded-full bg-md-surface flex items-center justify-center font-bold text-xl text-md-on-surface-variant border border-md-outline/10 shadow-sm"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <BiometricsPanelContent />
                                    </motion.div>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default LiPSandbox;
